import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, CalendarDays, HeartHandshake, Newspaper, Sparkles, Users, Instagram, UserCircle2 } from "lucide-react";
import DOMPurify from "dompurify";
import { AREE } from "@/data/aree";

// Renders HTML strings (from Quill) safely via DOMPurify.
// We normalise non-breaking spaces (U+00A0, `&nbsp;`, `&#160;`, `&#xa0;`) into
// regular spaces so pasted rich text (from Word / Notes / editors) doesn't
// create unbreakable lines on mobile.
const stripNbsp = (s) => (s || "")
  .replace(/\u00a0/g, " ")
  .replace(/&nbsp;/gi, " ")
  .replace(/&#160;/gi, " ")
  .replace(/&#xa0;/gi, " ");
const Html = ({ html, className = "" }) => (
  <div className={className} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(stripNbsp(html)) }} />
);

// Default fallback texts (used when no CMS value is set).
const D = {
  heroBadge: "Psicologə · Consulente in sessuologia · Autricə · Docente",
  heroTitle: `«Per conoscerci davvero, a volte dobbiamo <span class="italic font-normal">disimparare</span> ciò che pensavamo di <span class="text-fucsia">dover essere</span>.»`,
  heroPara1: "Questo è uno spazio per chi desidera costruire il proprio modo di essere, amare e relazionarsi.",
  heroPara2: "Offro consulenza psicologica e sessuologica per persone, coppie e reti affettive che desiderano esplorare identità, corpo, sessualità e relazioni oltre i modelli predefiniti. Un approccio affermativo e sex-positive, basato sull'ascolto, sull'autodeterminazione e sulla valorizzazione della pluralità delle esperienze umane.",
  heroImage: "/images/face.webp",
  heroAlbo: "Iscritto all'Albo degli Psicologi<br />della Lombardia · n. 22141",
  pronomiImage: "/images/pronomi.jpeg",
  pronomiTitle: `Sono una persona <span class="text-fucsia">non binaria</span> e uso tutti i pronomi.`,
  pronomiBody: `<p>Dove possibile, uso <span class="underline decoration-giallo decoration-2 underline-offset-4 font-medium">la schwa</span> per inclusività; dove la leggibilità lo richiede, uso <span class="underline decoration-fucsia decoration-2 underline-offset-4 font-medium">il femminile</span> (autrice, divulgatrice, ricercatrice).</p><p>Nelle conversazioni dirette, mi rivolgo a me <span class="underline decoration-arancio decoration-2 underline-offset-4 font-medium">al maschile</span>.</p><p class="italic text-viola/70 dark:text-cream/75">Tutte queste scelte riflettono il mio impegno verso la fluidità e l'autodeterminazione.</p>`,
  chisonoSubtitle: "Psicologə, consulente in sessuologia, docente e divulgatrice.",
  chisonoBody: `<p>Lavoro con adulti, coppie e reti affettive su temi legati a identità, desiderio, sessualità, consenso, relazioni non monogame e percorsi di affermazione e scoperta di sè. Mi occupo inoltre di formazione: insegno in diversi Master di sessuologia, formo professionistə sulla consulenza alle persone non monogame e porto avanti attività di divulgazione scientifica su relazioni, sessualità e salute mentale.</p><p>La mia prospettiva parte da un'idea semplice: il disagio psicologico non può essere compreso separando la persona dal contesto sociale, culturale e globale in cui vive. Per questo il mio lavoro non guarda soltanto al sintomo o alla difficoltà individuale, ma anche alle narrazioni, alle aspettative e ai modelli che abbiamo interiorizzato e che influenzano il modo in cui viviamo noi stessə, il nostro corpo e le nostre relazioni.</p>`,
  value1Title: "Spazio affermativo e non giudicante",
  value1Text: "Nessuna identità, orientamento, pratica consensuale o configurazione relazionale viene patologizzata. Uno spazio dove le esperienze plurali sono benvenute per definizione.",
  value2Title: "Approccio rigoroso ma critico",
  value2Text: "Riconosco che la psicologia occidentale ha una storia di violenza: patologizzazione di identità queer, sterilizzazione forzata, conversioni. Per questo la mia pratica clinica mantiene rigore scientifico ma è necessariamente decoloniale, critica verso le norme dominanti che sostengono il sistema, attenta alla dimensione politica di ogni scelta diagnostica e terapeutica.",
  value3Title: "Centrato sulla persona",
  value3Text: "Considero l'individuo come il principale esperto della propria esperienza, valorizzandone la soggettività, le risorse e i significati personali. Al tempo stesso, riconosco che il benessere e la sofferenza sono influenzati dalle molteplici dimensioni identitarie e dalle intersezioni tra privilegi e oppressioni che attraversano la vita della persona.",
  cta1Title: "Prenota il primo colloquio",
  cta1Body: "Individuali, di coppia, per relazioni plurime, supervisione clinica e formazione professionale.",
  cta2Title: "Un poliamore così grande",
  cta2Body: "Il mio libro sulle relazioni non monogame consensuali. Perché non c'è un solo modo di amare — edito da Edizioni Sonda.",
};

const VALUE_ICONS = [HeartHandshake, Sparkles, Users];

export default function Home() {
  const values = [
    { icon: VALUE_ICONS[0], title: D.value1Title, text: D.value1Text },
    { icon: VALUE_ICONS[1], title: D.value2Title, text: D.value2Text },
    { icon: VALUE_ICONS[2], title: D.value3Title, text: D.value3Text },
  ];

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-20 -right-20 h-96 w-96 rounded-full bg-fucsia/20 blur-3xl animate-blob" aria-hidden />
        <div className="absolute top-40 -left-20 h-80 w-80 rounded-full bg-giallo/30 blur-3xl animate-blob" style={{ animationDelay: "3s" }} aria-hidden />
        <div className="absolute bottom-0 right-1/4 h-64 w-64 rounded-full bg-arancio/30 blur-3xl animate-blob" style={{ animationDelay: "6s" }} aria-hidden />

        <div className="container-editorial relative pt-16 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <span className="pill" data-testid="hero-badge">{D.heroBadge}</span>
            <Html html={D.heroTitle} className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-black home-hero-title cms-heading" />
            <Html html={D.heroPara1} className="mt-8 max-w-2xl text-lg text-viola/80 leading-relaxed home-hero-para cms-para" />
            <Html html={D.heroPara2} className="mt-4 max-w-2xl text-viola/75 leading-relaxed home-hero-para cms-para" />
            <div className="mt-10 flex flex-wrap gap-3" data-testid="hero-ctas">
              <Link to="/consulenze" className="btn-primary" data-testid="cta-prenota"><CalendarDays size={16} /> Prenota una consulenza</Link>
              <Link to="/webinar" className="btn-secondary" data-testid="cta-webinar"><Sparkles size={16} /> Partecipa a un webinar</Link>
              <Link to="/blog" className="btn-ghost" data-testid="cta-blog"><Newspaper size={16} /> Leggi il blog</Link>
              <Link to="/libri" className="btn-ghost" data-testid="cta-libro"><BookOpen size={16} /> Scopri il libro</Link>
            </div>
            <a href="https://www.instagram.com/hello_policose/" target="_blank" rel="noopener noreferrer" className="mt-8 inline-flex items-center gap-2 text-sm text-viola/70 hover:text-fucsia" data-testid="hero-ig">
              <Instagram size={14} /> Divulgazione quotidiana su @hello_policose
            </a>
          </div>

          <div className="lg:col-span-4 relative">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden border border-viola/10 shadow-[0_30px_60px_-30px_rgba(44,20,69,0.35)]">
              <img
                src={D.heroImage}
                alt="Ritratto ravvicinato di Dania (Dan) Piras"
                className="h-full w-full object-cover"
                loading="eager"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-viola text-cream rounded-xl px-5 py-4 shadow-lg max-w-[260px]">
              <p className="text-xs uppercase tracking-wider text-giallo font-semibold">Albo</p>
              <Html html={D.heroAlbo} className="mt-1 text-sm leading-snug cms-para" />
            </div>
          </div>
        </div>
      </section>

      {/* PRONOMI & GENERE */}
      <section className="border-b border-viola/10 bg-cream dark:bg-viola/40" data-testid="section-pronomi">
        <div className="container-editorial py-16 lg:py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -top-4 -left-4 h-full w-full rounded-2xl bg-fucsia/20" aria-hidden />
              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border-2 border-viola/15 shadow-[0_30px_60px_-30px_rgba(44,20,69,0.35)]">
                <img
                  src={D.pronomiImage}
                  alt="Dania (Dan) Piras — persona non binaria"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full bg-viola text-cream px-4 py-1.5 text-xs font-semibold uppercase tracking-widest">
              <UserCircle2 size={14} />
              Pronomi e Genere
            </div>
            <Html html={D.pronomiTitle} className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight cms-heading" />
            <Html html={D.pronomiBody} className="mt-6 space-y-4 text-viola/80 dark:text-cream/85 text-lg leading-relaxed rich-body" />
          </div>
        </div>
      </section>

      {/* CHI SONO STRIP */}
      <section className="border-y border-viola/10 bg-lilla/40">
        <div className="container-editorial py-16 grid md:grid-cols-2 gap-10 items-start">
          <div>
            <span className="pill">Chi sono</span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight leading-tight">
              Dania (Dan) Piras
            </h2>
            <p className="mt-4 text-viola/70 text-lg">{D.chisonoSubtitle}</p>
          </div>
          <div className="text-viola/80">
            <Html html={D.chisonoBody} className="space-y-4 rich-body" />
            <Link to="/chi-sono" className="mt-4 inline-flex items-center gap-2 font-semibold text-fucsia hover:gap-3 transition-all">
              Scopri il mio percorso <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* AREE - Bento */}
      <section className="container-editorial py-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <span className="pill">Aree di lavoro</span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight">Di cosa mi occupo</h2>
          </div>
          <Link to="/aree-di-competenza" className="btn-ghost self-start md:self-auto" data-testid="link-aree">
            Esplora tutte <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {AREE.slice(0, 8).map((a, i) => (
            <Link
              to={`/aree/${a.slug}`}
              key={a.slug}
              className={`${a.color} rounded-2xl p-6 border border-viola/10 hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(44,20,69,0.25)] transition-transform duration-300 ${i === 0 || i === 5 ? "md:col-span-2" : ""}`}
              data-testid={`area-card-${a.slug}`}
            >
              <p className="text-[10px] font-semibold uppercase tracking-widest text-viola/60">{a.tag}</p>
              <h3 className="mt-2 font-display text-2xl leading-tight break-words hyphens-auto" lang="it">{a.title}</h3>
              <p className="mt-3 text-sm text-viola/75 line-clamp-3">{a.intro}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-fucsia">
                Approfondisci <ArrowRight size={14} />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-viola text-cream">
        <div className="container-editorial py-24 grid md:grid-cols-3 gap-10">
          {values.map((v) => (
            <div key={v.title}>
              <div className="grid h-12 w-12 place-items-center rounded-full bg-giallo text-viola">
                <v.icon size={20} />
              </div>
              <h3 className="mt-6 font-display text-2xl">{v.title}</h3>
              <Html html={v.text} className="mt-3 text-cream/80 leading-relaxed rich-body" />
            </div>
          ))}
        </div>
      </section>

      {/* SECONDARY CTA */}
      <section className="container-editorial py-24 grid md:grid-cols-2 gap-6">
        <Link to="/consulenze" className="group rounded-2xl bg-arancio/40 p-10 border border-viola/10 hover:-translate-y-1 transition-transform" data-testid="section-cta-consulenze">
          <p className="text-xs uppercase tracking-widest text-viola/60">01 — Consulenze online</p>
          <h3 className="mt-3 font-display text-3xl sm:text-4xl">{D.cta1Title}</h3>
          <Html html={D.cta1Body} className="mt-4 text-viola/80 rich-body cms-para" />
          <span className="mt-6 inline-flex items-center gap-2 font-semibold group-hover:gap-3 transition-all">Vai alle consulenze <ArrowRight size={16} /></span>
        </Link>
        <Link to="/libri" className="group rounded-2xl bg-giallo/50 p-10 border border-viola/10 hover:-translate-y-1 transition-transform overflow-hidden" data-testid="section-cta-libro">
          <div className="flex gap-6 items-start">
            <div className="flex-1">
              <p className="text-xs uppercase tracking-widest text-viola/60">02 — Pubblicazioni</p>
              <h3 className="mt-3 font-display text-3xl sm:text-4xl">{D.cta2Title}</h3>
              <Html html={D.cta2Body} className="mt-4 text-viola/80 rich-body cms-para" />
              <span className="mt-6 inline-flex items-center gap-2 font-semibold group-hover:gap-3 transition-all">Scopri il libro <ArrowRight size={16} /></span>
            </div>
            <img src="/images/book-cover.png" alt="Copertina del libro Un poliamore così grande" className="hidden sm:block w-28 h-auto rounded shadow-lg -mt-2" />
          </div>
        </Link>
      </section>
    </div>
  );
}
